import React, { useEffect, useState, useRef } from 'react';
import CardShell from 'components/CardShell';
import ReactTable from 'react-table';
import Button from 'components/ReactButton';
import { connect } from 'react-redux';
import API from 'helpers/API';
import { useHistory, useParams } from 'react-router-dom';
import Spinner from 'components/Spinner';
import { format } from 'date-fns';
import { closeSpinner as closeSpinnerAction, showSpinner as showSpinnerAction } from 'actions';
import { matchStringToDate } from 'helpers/datetime';
import vocabulary from 'constants/vocabulary';
import FilterBar from './FilterBar';
import styles from './styles.scss';
import ActveFilterBar from './ActiveFilterBar';
import typography from '../../../helpers/appTypography';
import TitleNavBlock from '../../../components/TitleNavBlock/TitleNavBlock';

const defaultFilters = {
  status: 'all',
  date: { from: 'any' },
  activeFilters: [],
};

function renderStatus(row) {
  const statusCode = row.value;
  const statusColor = statusCode >= 200 && statusCode <= 299 ? '#00854D' : '#C12547';
  return (
    <span>
      <span className={styles.status} style={{ background: statusColor }} />
      {row.value ? row.value : <span style={{ visibility: 'hidden' }}>XXX</span>}
    </span>
  );
}

export function sortByDateAscending(webhookLogs) {
  return [...webhookLogs].sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
}

function formatWebhookLogs(webhookLogs) {
  return webhookLogs.map(x => ({
    ...x,
    rawDate: x.date,
    date: format(new Date(x.date), 'DD/MM/YYYY HH:mm:ss'),
  }));
}

function WebhookLogs({ showSpinner, closeSpinner }) {
  const [data, setData] = useState({ webhookLogs: [], continuationToken: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [filtersButtonState, setFiltersButtonState] = useState(false);

  const history = useHistory();
  const { id } = useParams();
  const isInitialMount = useRef(true);
  const requestFiltersRef = useRef({});

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const urlStatus = params.get('status');
    const urlDateFrom = params.get('date_from');
    const urlDateTo = params.get('date_to');

    const requestFilters = {};
    const updatedFilters = {
      ...defaultFilters,
      date: { from: 'any' },
      activeFilters: [],
    };

    if (urlStatus) {
      requestFilters.status = urlStatus;
      updatedFilters.activeFilters.push({ name: 'Status', value: 'status' });
      updatedFilters.status = Object.keys(vocabulary).find(key => vocabulary[key].toLowerCase() === urlStatus);
    }

    if (urlDateFrom) {
      if (vocabulary[urlDateFrom]) {
        const filterDate = matchStringToDate(urlDateFrom);
        requestFilters.from = filterDate.from;
        requestFilters.to = filterDate.to;
      } else {
        requestFilters.from = urlDateFrom;
      }

      updatedFilters.date.from = urlDateFrom;
    }

    if (urlDateTo) {
      requestFilters.to = urlDateTo;
      updatedFilters.date.to = requestFilters.to;
      if (updatedFilters.date.from === defaultFilters.date.from) {
        updatedFilters.date.from = 'custom';
      }
    }

    if (urlDateFrom || urlDateTo) {
      updatedFilters.activeFilters.push({ name: 'Date', value: 'date' });
    }

    requestFiltersRef.current = requestFilters;

    const fetchData = async () => {
      if (!isInitialMount.current) {
        showSpinner();
      }
      setLoadError(null);

      try {
        const response = await API.getWebhookActivityLogs(id, requestFilters);
        setData({
          webhookLogs: sortByDateAscending(formatWebhookLogs(response.webhookLogs)),
          continuationToken: response.continuationToken,
        });
      } catch (e) {
        setLoadError('There was a problem loading webhook logs. Please try again.');
      } finally {
        if (isInitialMount.current) {
          isInitialMount.current = false;
          setIsLoading(false);
        } else {
          closeSpinner();
        }
      }
    };

    fetchData();
    setFilters(updatedFilters);
  }, [id, showSpinner, closeSpinner, history.location.search]);

  async function loadMore() {
    setIsLoadingMore(true);
    setLoadError(null);

    try {
      const response = await API.getWebhookActivityLogs(id, requestFiltersRef.current, data.continuationToken);
      setData(previous => ({
        webhookLogs: sortByDateAscending([...previous.webhookLogs, ...formatWebhookLogs(response.webhookLogs)]),
        continuationToken: response.continuationToken,
      }));
    } catch (e) {
      setLoadError('There was a problem loading more webhook logs. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  }

  function removeActiveFilter(filter) {
    const params = new URLSearchParams(history.location.search);

    // eslint-disable-next-line default-case
    switch (filter) {
      case 'status':
        params.delete(filter);
        break;
      case 'date':
        params.delete('date_from');
        params.delete('date_to');
    }
    history.push(({
      pathname: 'logs',
      search: `?${params.toString()}`,
    }));
  }

  function applyFilters(formFilters) {
    const params = new URLSearchParams();
    if (formFilters.status) {
      params.set('status', formFilters.status);
    }

    if (formFilters.date && formFilters.date.from && formFilters.date.from !== 'custom') {
      params.set('date_from', formFilters.date.from);
    }

    if (formFilters.date && formFilters.date.to) {
      params.set('date_to', formFilters.date.to);
    }

    history.push(({
      pathname: 'logs',
      search: `?${params.toString()}`,
    }));
  }

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div>
      <TitleNavBlock
        title={typography.webhookLogs.title}
        subTitle={typography.webhookLogs.subTitle}
        onBack={() => window.history.back()}
      />
      <FilterBar
        status={filters.status}
        date={filters.date}
        onApply={applyFilters}
        filtersButtonState={filtersButtonState}
        setFiltersButtonState={setFiltersButtonState}
      />
      <ActveFilterBar
        filters={filters.activeFilters}
        onClose={removeActiveFilter}
        setFiltersButtonState={setFiltersButtonState}
        customBtnName="+ Add webhook filters"
      />
      {loadError && <div className={styles.error}>{loadError}</div>}
      {
        data.webhookLogs.length
          ? (
            <CardShell>
              <ReactTable
                className="webhooks"
                data={data.webhookLogs}
                columns={[
                  {
                    Header: 'Id',
                    accessor: 'id',
                    show: false,
                  },
                  {
                    Header: 'Status',
                    accessor: 'statusCode',
                    Cell: row => renderStatus(row),
                    maxWidth: 150,
                  },
                  {
                    Header: 'URL',
                    accessor: 'url',
                  },
                  {
                    Header: 'Elapsed Time',
                    accessor: 'elapsedTime',
                    maxWidth: 200,
                  },
                  {
                    Header: 'Date',
                    accessor: 'date',
                    maxWidth: 200,
                  },
                ]}
                getTdProps={(state, rowInfo) => ({
                  onClick: () => history.push(`logs/${rowInfo.row.id}`),
                })}
                sortable={false}
                footerText="records"
                minRows={0}
                showPagination={false}
                pageSize={data.webhookLogs.length}
                trackingComponentName="Webhook Logs"
              />
              {
                data.continuationToken && (
                  <div className={styles.loadMoreRow}>
                    <Button
                      theme="grey"
                      onClick={loadMore}
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore ? 'Loading...' : 'Load more'}
                    </Button>
                  </div>
                )
              }
            </CardShell>
          )
          : <div>There were no records found based on filters applied</div>
      }
    </div>
  );
}

export default connect(null, { showSpinner: showSpinnerAction, closeSpinner: closeSpinnerAction })(WebhookLogs);
